import { Box, Typography } from '@mui/material';
import type { BeatDto } from '../../api/queries/storyArchitect';

interface EmotionalArcChartProps {
  beats: BeatDto[];
}

export function EmotionalArcChart({ beats }: EmotionalArcChartProps) {
  const data = beats
    .sort((a, b) => a.sequenceOrder - b.sequenceOrder)
    .map((b) => ({
      name: b.name,
      intensity: b.emotionalIntensity ?? 5,
    }));

  if (data.length < 2) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          Add at least 2 beats with emotional intensity to see the arc.
        </Typography>
      </Box>
    );
  }

  // Simple SVG line chart (no external dependency needed)
  const width = 600;
  const height = 120;
  const padding = { top: 10, right: 20, bottom: 25, left: 30 };
  const chartW = width - padding.left - padding.right;
  const chartH = height - padding.top - padding.bottom;

  const points = data.map((d, i) => ({
    x: padding.left + (i / (data.length - 1)) * chartW,
    y: padding.top + chartH - (d.intensity / 10) * chartH,
    label: d.name,
    value: d.intensity,
  }));

  const linePath = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`)
    .join(' ');

  return (
    <Box sx={{ width: '100%', overflowX: 'auto' }}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        style={{ width: '100%', maxHeight: 140 }}
      >
        {/* Grid lines */}
        {[0, 2.5, 5, 7.5, 10].map((v) => {
          const y = padding.top + chartH - (v / 10) * chartH;
          return (
            <g key={v}>
              <line
                x1={padding.left}
                y1={y}
                x2={width - padding.right}
                y2={y}
                stroke="#333"
                strokeWidth={0.5}
                opacity={0.3}
              />
              <text
                x={padding.left - 5}
                y={y + 3}
                textAnchor="end"
                fontSize={8}
                fill="#888"
              >
                {v}
              </text>
            </g>
          );
        })}

        {/* Line */}
        <path d={linePath} fill="none" stroke="#2196f3" strokeWidth={2} />

        {/* Area fill */}
        <path
          d={`${linePath} L ${points[points.length - 1].x} ${padding.top + chartH} L ${points[0].x} ${padding.top + chartH} Z`}
          fill="#2196f3"
          opacity={0.1}
        />

        {/* Points */}
        {points.map((p, i) => (
          <g key={i}>
            <circle cx={p.x} cy={p.y} r={4} fill="#2196f3" />
            <text
              x={p.x}
              y={padding.top + chartH + 15}
              textAnchor="middle"
              fontSize={7}
              fill="#aaa"
            >
              {p.label.length > 10 ? p.label.slice(0, 10) + '...' : p.label}
            </text>
          </g>
        ))}
      </svg>
    </Box>
  );
}
